import {GeoCoord} from "./geo-coord";
import {SiteEntity} from "./site";
import {VehicleEntity} from "./vehicle";
import {TrailerEntity} from "./trailer";
import {RigEntity} from "./rig";
import {GeofenceEventEntity} from "./geofence-event";
import {PersonEntity} from "./person";
import {DriverLocationEntity} from "./driver-location";
import {TransportEntity} from "./transport";
import {ServiceTypeRuleEntity} from "./service-type-rule";
import {GeofenceEntity} from "./geofence";
import {ShiftEntity} from "./shift";

/**
 * Implement this interface when creating an entity.
 */
export interface Entity {
  id: string;
  readonly entityType: EntityType;
  readonly displayName: string;
  doNotUse?: boolean
  abacAttributes?: Record<string, any>
}

export interface EntityAssoc {
  objectId: string;
  objectType: string;
  objectName?: string;
}

export type EntityObject = Entity | EntityAssoc;

/**
 * Implement this interface when creating an entity.
 */
export interface EntityIdentifier {
  id: string;
  type: string;
}

export interface MultiEntityIdentifier {
  ids: string[];
  type: string;
}

export const fromEntityIdentifier = (value: EntityIdentifier): EntityObject => {
  return {
    objectId: value.id,
    objectType: value.type,
  };
};

export const getEntityIdentifier = (value: EntityObject): EntityIdentifier => {
  let eid: EntityIdentifier;

  if (isEntity(value)) {
    eid = {
      id: value.id,
      type: value.entityType,
    };
  } else {
    eid = {
      id: value.objectId,
      type: value.objectType,
    };
  }

  return eid;
};

export const getEntityIdentifierOrUndefined = (
  value: EntityObject | undefined
): EntityIdentifier | undefined => {
  let eid: EntityIdentifier | undefined = undefined;

  if (value) {
    if (isEntity(value)) {
      eid = {
        id: value.id,
        type: value.entityType,
      };
    } else {
      eid = {
        id: value.objectId,
        type: value.objectType,
      };
    }
  }

  return eid;
};

/**
 * Implement this interface if the entity or object is locatable.
 */
export interface Locatable {
  readonly latLng?: GeoCoord;
}

/**
 * A locatable entity.
 */
export type LocatableEntity = Entity & Locatable;

const ALL_ENTITY_TYPES = [
  'SITE',
  'VEHICLE',
  'TRAILER',
  'RIG',
  'PERSON',
  'PERSONCAP',
  'DRIVERLOCATION',
  'TRANSPORT',
  'GEOFENCEEVENT',
  'TRANSPORTTRANSPORTASSOC',
  'LEGACYPIPELINEFLOW',
  'REGION',
  'CONTAINER',
  'COMPANY',
  'LEGACYPIPELINE',
  'LEGACYPIPELINEMETER',
  'OPERATINGAREA',
  'FREIGHTTYPE',
  'WELL',
  'WELLSTAGE',
  'WELLSTAGES',
  'ROUTE',
  'SITEASSIGN',
  'SITEASSIGNCOMPASSOC',
  'SITEASSIGNSITEASSOC',
  'SITE_RESTRICTION',
  'RESTRICTION_SCHED_WINDOW',
  'RATEMODEL',
  'WATERTYPE',
  'SERVICETYPE',
  'CONCOMMIGLED',
  'SERVICEWATERASSOC',
  'DISPATCHREQUEST',
  'MESSAGE',
  'DISPATCH',
  'ROUTEGROUP',
  'PIPE',
  'PIPELINEGRAPH',
  'PIPELINEGRAPHEDGE',
  'PIPELINEGRAPHNODE',
  'PIPE_FLOW',
  'PIPE_FLOW_TRANSPORT',
  'METER',
  'METER_FLOW_READING',
  'UNITOFMEASURE',
  'REGION',
  'SERVICETYPERULE',
  'GEOFENCE',
  'SHIFT',
  'ALLOCATABLE_LOAD_SETTINGS',
  'ALLOCATABLE_LOAD',
  'PROD_VOL',
  'METER_PROD_VOL_READING',
  'SITESITEASSOC',
  "METER_FLOW_VOLUME",
  "OBJECTDOCASSOC",
  "OBJECTPICASSOC",
  "INVOICE",
  "TLI",
  "TICKETCODE",
  "SETTLEMENT",
  "COMPANY_SETTLEMENT",
  "ACTIVITYPERIOD",
  "OBJECT_PREF",
  "WATERGO_PREF",
  "OILGO_PREF",
  "COMPANY_UNIT",
  "TRANSPORTGAUGE",
  "INVACCTLOOKUP",
  "CONVOLDROP",
  "DVIR",
  "PERMISSION",
  "ROLE",
  "ABAC_POLICY",
  "GEOSITEGROUP",
  "RRCLOCPROFILE",
  "RRCCOMPANYPROFILE",
  "RRCEP9WASTEPROFILE",
  "RRCEP10WASTEMFST",
  "RRCEP10WASTEMFSTBLDSTATUS",
  "SVCEVENT",
  "SVCEVENTSERVICETYPE",
  "SVCEVENTTGT",
  "SVCEVENTTICKETPOLICY",
  "SVCEVENTTICKETSCOPE",
  "SERVICETYPEFACET",
  "SERVICETYPEFACETMODE",
  "SERVICETYPEMODE",
  "SVCEVENTTYPESERVICETYPE",
  "SVCEVENTTARGET",
  "FLEETASSETCAPAB",
  "LOCSTATUSFACET",
  "NOTIF_TEMPLATE",
  "NOTIF_TEMP_AUTO_SUB_POL",
  "NOTIF_TEMP_SCOPE_AUD_CAP",
  "NOTIF_TEMP_SCOPE_COMPANY",
  "NOTIF_TEMP_SCOPE_REGION",
  "NOTIF_SEND_PROF",
  "NOTIF_SUB",
  "CUSTOMFIELDDEF",
  "CUSTOMFIELDPROFILE",
  "CUSTOMFIELDPROFILEITEM",
  "DVIRCUSTOMFIELDVALUE",
  "SYSPROCEXEC",
  "SYSPROCEXECARTIFACT",
  "SYSPROCEXECISSUE",
  "SYSPROCEXECMETRIC",
  "SYSPROCEXECMISSINGMAPPING",
  "SYSTEMMISSINGMAPPING",
  "NOTICE",
  "TAGASSIGNMENT",
  "TAGCATEGORY",
  "TAGDEFINITION",
  "TAGDEFINITIONCATEGORY",
  "NOTIFRECVPROFILE",
  "NOTIFTEMPLTHREADCFG",
  "TEAM_MEMBER"
] as const;

export type EntityType = typeof ALL_ENTITY_TYPES[number];

export const isEntity = (value: EntityObject | Partial<EntityObject>): value is Entity => {
  return (value as Entity).id !== undefined;
};

export const isLocatableEntity = (value: Entity): value is LocatableEntity => {
  return (value as LocatableEntity).latLng !== undefined;
};

export const getObjectId = (
  value: EntityObject | string | undefined
): string | undefined => {
  let id: string | undefined = undefined;

  if (value) {
    if (typeof value === 'string') {
      id = value;
    } else if (isEntity(value)) {
      id = value.id;
    } else {
      id = value.objectId;
    }
  }

  return id;
};

export const getRequiredObjectId = (value: EntityObject | string): string => {
  let id: string;

  if (typeof value === 'string') {
    id = value;
  } else if (isEntity(value)) {
    id = value.id;
  } else {
    id = value.objectId;
  }

  return id;
};

export const getObjectType = (
  value: EntityObject | string | undefined
): string | undefined => {
  let type: string | undefined = undefined;

  if (value) {
    if (typeof value === 'string') {
      type = value;
    } else if (isEntity(value)) {
      type = value.entityType;
    } else {
      type = value.objectType;
    }
  }

  return type;
};

export const getRequiredObjectType = (value: EntityObject | string): string => {
  let type: string;

  if (typeof value === 'string') {
    type = value;
  } else if (isEntity(value)) {
    type = value.entityType;
  } else {
    type = value.objectType;
  }

  return type;
};

export const getObjectName = (
  value: EntityObject | string | undefined
): string | undefined => {
  let name: string | undefined = undefined;

  if (value) {
    if (typeof value === 'string') {
      name = value;
    } else if (isEntity(value)) {
      name = value.displayName;
    } else {
      name = value.objectName;
    }
  }

  return name;
};

export function getRequiredEntityId<T extends Entity>(
  value: T | string
): string {
  let id: string;

  if (typeof value === 'string') {
    id = value;
  } else {
    id = value.id;
  }

  return id;
}

export const toEntityObject = (
  value: Partial<EntityAssoc> | undefined
): EntityObject | undefined => {
  let entity: EntityObject | undefined = undefined;

  if (value && value.objectId && value.objectType) {
    entity = {
      objectId: value.objectId,
      objectType: value.objectType,
      objectName: value.objectName,
    };
  }

  return entity;
};

export const isEntityType = (value: string): value is EntityType => {
  return ALL_ENTITY_TYPES.map((e) => e as string).includes(value);
};

export const isSite = (value: Entity): value is SiteEntity => {
  return value.entityType === 'SITE';
};

export const isVehicle = (value: Entity): value is VehicleEntity => {
  return value.entityType === 'VEHICLE';
};

export const isTrailer = (value: Entity): value is TrailerEntity => {
  return value.entityType === 'TRAILER';
};

export const isRig = (value: Entity): value is RigEntity => {
  return value.entityType === 'RIG';
};

export const isPerson = (value: Entity): value is PersonEntity => {
  return value.entityType === 'PERSON';
};

export const isGeofenceEvent = (
  value: Entity
): value is GeofenceEventEntity => {
  return value.entityType === 'GEOFENCEEVENT';
};

export const isDriverLocation = (
  value: Entity
): value is DriverLocationEntity => {
  return value.entityType === 'DRIVERLOCATION';
};

export const isTransport = (value: Entity): value is TransportEntity => {
  return value.entityType === 'TRANSPORT';
};

export const isServiceTypeRule = (
  value: Entity
): value is ServiceTypeRuleEntity => {
  return value.entityType === 'SERVICETYPERULE';
};

export const isGeofence = (value: Entity): value is GeofenceEntity => {
  return value.entityType === 'GEOFENCE';
};

export const isShift = (value: Entity): value is ShiftEntity => {
  return value.entityType === 'SHIFT';
};
